import { createContext, Dispatch, FC, useContext, useReducer, useCallback, ReactNode } from 'react'
import { User } from '../interfaces/Types'
import { UserModuleProfile } from '../interfaces/ModuleTypes'


type State = {
  isLMSAvailable: boolean
  user: User | null
  userProfile: UserModuleProfile | null
  errorMessage: string
}

const initialState: State = {
  isLMSAvailable: false,
  user: null,
  userProfile: null,
  errorMessage: ''
}

const Context = createContext<{
  sessionState: State
  dispatch: Dispatch<Action>
}>({ sessionState: initialState, dispatch: () => null })

enum ActionType {
  SET_USER,
  SET_PROFILE,
  SET_ERROR_MSG,
  SET_LMS_AVAILABLE
}

type Action =
  {
    type: ActionType.SET_USER
    payload: User
  } |
  {
    type: ActionType.SET_PROFILE
    payload: UserModuleProfile
  } |
  {
    type: ActionType.SET_ERROR_MSG
    payload: string
  } |
  {
    type: ActionType.SET_LMS_AVAILABLE
    payload: boolean
  }

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case ActionType.SET_USER:
      return { ...state, user: action.payload } as State
    case ActionType.SET_PROFILE:
      return { ...state, userProfile: action.payload } as State
    case ActionType.SET_ERROR_MSG:
      return { ...state, errorMessage: action.payload } as State
    case ActionType.SET_LMS_AVAILABLE:
      return { ...state, isLMSAvailable: action.payload } as State
    default:
      return state
  }
}

const SessionContextProvider: FC<{ children: ReactNode }> = ({ children }: { children: ReactNode }) => {
  const [sessionState, dispatch] = useReducer(reducer, initialState)

  return (
    <Context.Provider value={{ sessionState, dispatch }}>
      {children}
    </Context.Provider>
  )
}

const useSessionContext = () => {
  const context = useContext(Context)
  if (!context) {
    throw new Error(
      'useSessionContext must be used within a SessionContextProvider. Wrap a parent component in <SessionContextProvider> to fix this error.'
    )
  }

  const { sessionState: { isLMSAvailable, user, userProfile, errorMessage }, dispatch } = context

  const actions = {
    setUser: useCallback((user: User) =>
      dispatch({ type: ActionType.SET_USER, payload: user }), [dispatch]),
    setProfile: useCallback((userProfile: UserModuleProfile) =>
      dispatch({ type: ActionType.SET_PROFILE, payload: userProfile }), [dispatch]),
    setErrorMsg: useCallback((errorMessage: string) =>
      dispatch({ type: ActionType.SET_ERROR_MSG, payload: errorMessage }), [dispatch]),
    setLMSAvailable: useCallback((isLMSAvailable: boolean) =>
      dispatch({ type: ActionType.SET_LMS_AVAILABLE, payload: isLMSAvailable }), [dispatch]),
  }

  return { isLMSAvailable, user, userProfile, errorMessage, sessionActions: actions }
}
export default useSessionContext

export { SessionContextProvider }